import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOWrapper } from "@/components/SEOWrapper";
import { AuthorBox } from "@/components/insight/AuthorBox";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Crumbs, Section } from "@/components/luminous/interior";
import { Reveal } from "@/components/luminous/primitives";

import { clusters, insights, insightsByCluster } from "@/data/insights";
import { ArrowRight } from "lucide-react";

const copy = {
  es: {
    kicker: "Luis ROC Search Library",
    h1: "Search Library: guías, análisis y marcos de decisión para búsqueda",
    title: "Search Library | Guías y análisis de SEO — Luis ROC",
    description:
      "Biblioteca de trabajo sobre SEO técnico, AI Search, SEO internacional, mercado hispano de EE. UU., ecommerce y Amazon. Marcos de decisión, no recetas.",
    lead: "Material de trabajo, no contenido de relleno. Cada pieza existe para resolver una decisión concreta: qué priorizar, qué medir y qué no merece inversión.",
    crumb: "Biblioteca",
    home: "Inicio",
    featuredLabel: "Recurso destacado",
    serviceLabel: "Servicio relacionado",
    empty: "En preparación.",
    readingSuffix: "min",
    otherLabel: "Otros recursos",
    other: [
      { label: "Herramientas SEO", href: "/herramientas-seo" },
      { label: "Servicios", href: "/servicios" },
      { label: "Metodología", href: "/metodologia" },
      { label: "Blog", href: "/blog" },
    ],
  },
  en: {
    kicker: "Luis ROC Search Library",
    h1: "Search Library: search guides, analysis and playbooks",
    title: "Search Library | SEO guides and analysis — Luis ROC",
    description:
      "A working library on technical SEO, AI Search, international SEO, the US Hispanic market, ecommerce and Amazon. Decision frameworks, not recipes.",
    lead: "Working material, not filler. Each piece exists to settle one decision: what to prioritise, what to measure and what is not worth funding.",
    crumb: "Library",
    home: "Home",
    featuredLabel: "Featured resource",
    serviceLabel: "Related service",
    empty: "In progress.",
    readingSuffix: "min",
    otherLabel: "Other resources",
    other: [
      { label: "SEO tools", href: "/en/seo-tools" },
      { label: "Services", href: "/en/services" },
      { label: "Methodology", href: "/en/methodology" },
      { label: "Blog", href: "/en/blog" },
    ],
  },
} as const;

const LibraryHubPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const t = copy[language];

  const featured = insights[0];
  const rest = insights.slice(1);

  return (
    <SEOWrapper
      title={t.title}
      description={t.description}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      localBusiness={false}
      summary={t.lead}
      structuredData={{
        type: "collection",
        data: {
          name: t.h1,
          description: t.description,
          inLanguage: language,
          url,
          hasPart: insights.map((i) => ({
            headline: i[language].h1,
            url: `https://luisroc.com${i.path[language]}`,
          })),
        },
      }}
    >
      <div className="min-h-screen bg-[color:var(--v3-mineral)]">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          <header className="border-b border-[color:var(--v3-line)] bg-[color:var(--v3-mineral)]">
            <div className="v3-shell pb-10 pt-8 lg:pb-14 lg:pt-12">
              <Crumbs items={[{ label: t.home, to: language === "es" ? "/" : "/en" }, { label: t.crumb }]} />
              <p className="v3-micro mt-7">{t.kicker}</p>
              <h1
                className="v3-display mt-4 max-w-3xl"
                style={{ fontSize: "clamp(1.9rem, 3.4vw, 3rem)" }}
              >
                {t.h1}
              </h1>
              <p className="v3-body mt-6 text-[1.05rem]">{t.lead}</p>
            </div>
          </header>

          {/* Recurso destacado: composición amplia, sin cuadrícula */}
          {featured ? (
            <Section tone="white" label={t.featuredLabel}>
              <Reveal>
                <Link
                  to={featured.path[language]}
                  className="group block border-t-2 border-[color:var(--v3-blue)] pt-7 transition-colors"
                >
                  <div className="grid gap-6 lg:grid-cols-12 lg:gap-14">
                    <div className="lg:col-span-8">
                      <p className="v3-micro">
                        {featured[language].eyebrow}
                        <span className="mx-2" aria-hidden>·</span>
                        {featured.readingMinutes} {t.readingSuffix}
                      </p>
                      <h2
                        className="v3-display mt-4 transition-colors group-hover:text-[color:var(--v3-blue)]"
                        style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.4rem)" }}
                      >
                        {featured[language].h1}
                      </h2>
                    </div>
                    <div className="lg:col-span-4 lg:pt-1">
                      <p className="v3-body">{featured[language].description}</p>
                      <span className="v3-inline-link mt-5">
                        {language === "es" ? "Leer" : "Read"}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            </Section>
          ) : null}

          {/* Índice secundario editorial por cluster */}
          {clusters.map((cluster) => {
            const items = insightsByCluster(cluster.id).filter((i) => rest.includes(i));
            if (items.length === 0) return null;

            return (
              <Section key={cluster.id} label={cluster[language].name}>
                <div className="grid gap-8 lg:grid-cols-12 lg:gap-14">
                  <div className="lg:col-span-4">
                    <p className="v3-note">{cluster[language].description}</p>
                    <p className="v3-micro mt-5">{t.serviceLabel}</p>
                    <Link
                      to={cluster.moneyPage[language]}
                      className="v3-inline-link mt-1"
                    >
                      {cluster[language].name}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  </div>

                  <ul className="lg:col-span-8">
                    {items.map((i) => (
                      <li key={i.id} className="border-t border-[color:var(--v3-line)] first:border-t-0">
                        <Link to={i.path[language]} className="group block py-5">
                          <p className="v3-micro">
                            {i[language].eyebrow}
                            <span className="mx-2" aria-hidden>·</span>
                            {i.readingMinutes} {t.readingSuffix}
                          </p>
                          <h3 className="mt-2 text-[1.05rem] font-semibold leading-snug text-[color:var(--v3-ink)] transition-colors group-hover:text-[color:var(--v3-blue)]">
                            {i[language].h1}
                          </h3>
                          <p className="v3-note mt-2">{i[language].description}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>
            );
          })}

          <Section tone="white" label={t.otherLabel}>
            <ul className="flex flex-wrap gap-x-8 gap-y-3">
              {t.other.map((o) => (
                <li key={o.href}>
                  <Link to={o.href} className="v3-inline-link">
                    {o.label}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <div className="v3-shell v3-section-tight">
            <AuthorBox language={language} />
          </div>
        </main>

        <Footer language={language} />
      </div>
    </SEOWrapper>
  );
};

export default LibraryHubPage;
